import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Download, Coins, Phone, CheckCircle2, Search, X } from "lucide-react";
import { ManualLeadModal } from "./ManualLeadModal";

const STATUT_BADGE = {
  nouveau:  { label: "En cours", className: "bg-[#E1ECFF] text-[#1E50C9] border-[#B9D2FF]" },
  rappele:  { label: "En cours", className: "bg-[#E1ECFF] text-[#1E50C9] border-[#B9D2FF]" },
  converti: { label: "Souscrit", className: "bg-[#D7F8EA] text-[#0A8A57] border-[#A9F0CE]" },
  perdu:    { label: "Perdu",    className: "bg-[#E8EAEC] text-[#3C4649] border-[#BFC6CC]" },
};
const STATUT_FILTERS = [
  { value: "all", label: "Tous" },
  { value: "en_cours", label: "En cours" },
  { value: "converti", label: "Souscrits" },
];

function fmtPhone(p) { return (p || "").replace(/(\d{2})(?=\d)/g, "$1 ").trim(); }

function CagnotteBar({ total, used, bonus }) {
  const t = total || 0;
  const b = bonus || 0;
  const u = used || 0;
  const cap = t + b;
  const remaining = Math.max(cap - u, 0);
  const pct = cap > 0 ? Math.min((u / cap) * 100, 100) : 0;
  return (
    <div className="min-w-[160px]">
      <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
        <div className="h-full bg-[#10D183] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1 text-xs">
        <span className="text-[#687175]">{u}€ / {cap}€{b > 0 ? <span title={`Base 30 € + ${b}€ parrainage`} className="text-[#F9C80E] ml-1">+{b}€ parrainage</span> : null}</span>
        <span className={`font-semibold ${remaining > 0 ? "text-[#0A8A57]" : "text-[#939BA0]"}`}>
          {remaining > 0 ? `${remaining}€ restant` : "Épuisée"}
        </span>
      </div>
    </div>
  );
}

export function ClientsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [purchaseLead, setPurchaseLead] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [manualLeadOpen, setManualLeadOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // For commerçant : "en_cours" groups nouveau+rappele (client-side), exclude perdu by default
      const params = {};
      if (filter === "converti") params.statut = "converti";
      const { data } = await api.get("/leads", { params });
      let items = data || [];
      if (filter === "en_cours") {
        items = items.filter((l) => l.statut === "nouveau" || l.statut === "rappele");
      } else if (filter === "all") {
        items = items.filter((l) => l.statut !== "perdu");
      }
      setLeads(items);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  const filteredLeads = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const hay = `${l.prenom || ""} ${l.nom || ""} ${l.email || ""} ${l.telephone || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [leads, query]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("statut", filter);
      const url = `${api.defaults.baseURL}/leads/export.csv${params.toString() ? "?" + params : ""}`;
      const token = localStorage.getItem("pde_token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `leads_pde_${new Date().toISOString().slice(0,10).replace(/-/g,"")}.csv`;
      a.click();
      toast.success("Export téléchargé");
    } catch (err) { toast.error("Échec de l'export"); }
  };

  const convert = async (id) => {
    try {
      await api.patch(`/leads/${id}/statut`, { statut: "converti" });
      toast.success("Lead converti — cagnotte 30 € créée");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openPurchase = (lead) => {
    setPurchaseLead(lead);
    setAmount("");
    setNote("");
  };

  const submitPurchase = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const amt = parseFloat(amount);
      if (!(amt > 0)) throw new Error("Montant invalide");
      await api.post(`/leads/${purchaseLead.id}/purchase`, { amount: amt, note: note || undefined });
      toast.success(`Achat de ${amt}€ enregistré`);
      setPurchaseLead(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="clients-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#191D1E]">Mes clients</h2>
          <p className="text-sm text-[#687175] mt-0.5">Vos clients et leurs bons d'achat associés à votre commerce.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reload} data-testid="clients-refresh" className="rounded-xl border-[#E5E7EB] hover:bg-[#F5F6F9]">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
          <Button onClick={() => setManualLeadOpen(true)} data-testid="clients-manual-create" className="rounded-xl bg-[#1B4FA6] hover:bg-[#143C82] text-white font-semibold ml-2">
            <Phone className="w-4 h-4 mr-2" /> Nouveau client (appel)
          </Button>
          <Button onClick={exportCsv} data-testid="clients-export-csv" className="bg-[#397DFF] hover:bg-[#2860D8] text-white rounded-xl">
            <Download className="w-4 h-4 mr-2" /> Exporter CSV
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap mb-4" data-testid="clients-filters">
        {STATUT_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            data-testid={`clients-filter-${f.value}`}
            className={[
              "px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
              filter === f.value
                ? "bg-[#397DFF] text-white border-[#397DFF]"
                : "bg-white text-[#687175] border-[#E5E7EB] hover:bg-[#F5F6F9]",
            ].join(" ")}
          >{f.label}</button>
        ))}
      </div>

      <div className="relative mb-3" data-testid="clients-search-wrap">
        <Search className="w-4 h-4 text-[#939BA0] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un client par nom, prénom, email ou téléphone…"
          data-testid="clients-search-input"
          className="pl-9 pr-9 h-10 rounded-xl border-[#E5E7EB] focus-visible:ring-[#1B4FA6]/40 focus-visible:border-[#1B4FA6]"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            data-testid="clients-search-clear"
            aria-label="Effacer la recherche"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-[#687175] hover:text-[#1F2937] hover:bg-[#F5F6F9] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="text-xs text-[#687175] mb-3" data-testid="clients-count">
        {query.trim()
          ? `${filteredLeads.length} / ${leads.length} leads affichés`
          : `${leads.length} lead${leads.length > 1 ? "s" : ""} affiché${leads.length > 1 ? "s" : ""}`}
      </div>

      {leads.length === 0 && !loading && (
        <div className="py-12 text-center text-sm text-[#687175]" data-testid="clients-empty">Aucun client pour ce filtre.</div>
      )}
      {leads.length > 0 && filteredLeads.length === 0 && (
        <div className="py-12 text-center text-sm text-[#687175]" data-testid="clients-search-empty">
          Aucun client ne correspond à votre recherche.
        </div>
      )}

      {filteredLeads.length > 0 && (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#687175] border-b border-[#E5E7EB]">
                <th className="px-2 py-2">Client</th>
                <th className="px-2 py-2">Téléphone</th>
                <th className="px-2 py-2">Date</th>
                <th className="px-2 py-2">Statut</th>
                <th className="px-2 py-2">Bon d'achat</th>
                <th className="px-2 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((l) => {
                const badge = STATUT_BADGE[l.statut] || STATUT_BADGE.nouveau;
                const cag = l.cagnotte || { total: 0, used: 0, parrainage_bonus: 0 };
                const cap = (cag.total || 0) + (cag.parrainage_bonus || 0);
                const remaining = cap - (cag.used || 0);
                return (
                  <tr key={l.id} className="border-b border-[#EEF0F4] last:border-0" data-testid={`client-row-${l.id}`}>
                    <td className="px-2 py-3">
                      <div className="font-semibold text-[#191D1E]">{l.prenom} {l.nom}</div>
                      <div className="text-xs text-[#687175]">{l.code_postal}</div>
                    </td>
                    <td className="px-2 py-3 text-[#687175] whitespace-nowrap">
                      <a href={`tel:${l.telephone}`} className="hover:text-[#397DFF]">{fmtPhone(l.telephone)}</a>
                    </td>
                    <td className="px-2 py-3 text-xs text-[#687175]">{new Date(l.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-2 py-3"><Badge className={`${badge.className} border`}>{badge.label}</Badge></td>
                    <td className="px-2 py-3">
                      {l.statut === "converti"
                        ? <CagnotteBar total={cag.total} used={cag.used} bonus={cag.parrainage_bonus} />
                        : <span className="text-xs text-[#939BA0]">—</span>}
                    </td>
                    <td className="px-2 py-3 text-right whitespace-nowrap">
                      {l.statut !== "converti" && l.statut !== "perdu" && (
                        <span className="text-xs text-[#939BA0] italic">Suivi par votre agence</span>
                      )}
                      {l.statut === "converti" && remaining > 0 && (
                        <Button size="sm" onClick={() => openPurchase(l)} data-testid={`client-purchase-${l.id}`} className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] rounded-lg text-xs font-semibold">
                          <Coins className="w-3.5 h-3.5 mr-1" /> Enregistrer un achat
                        </Button>
                      )}
                      {l.statut === "converti" && remaining === 0 && (
                        <Badge className="bg-[#E8EAEC] text-[#3C4649] border border-[#BFC6CC]">Cagnotte épuisée</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && leads.length > 0 && (
        <p className="text-xs text-[#687175] italic mt-4 max-w-3xl" data-testid="commercant-info-note">
          💡 La validation de la souscription est gérée par votre agence Place des Énergies. Dès qu'un client souscrit, son bon d'achat est automatiquement crédité ici.
        </p>
      )}

      <Dialog open={!!purchaseLead} onOpenChange={(v) => !v && setPurchaseLead(null)}>
        <DialogContent data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Enregistrer un achat</DialogTitle>
            <DialogDescription>
              {purchaseLead && (() => {
                const c = purchaseLead.cagnotte || {};
                const cap = (c.total || 0) + (c.parrainage_bonus || 0);
                const rem = cap - (c.used || 0);
                return <>Client : <strong>{purchaseLead.prenom} {purchaseLead.nom}</strong> · Restant : <strong>{rem}€</strong>{(c.parrainage_bonus || 0) > 0 ? <> (dont <strong>{c.parrainage_bonus}€</strong> bonus parrainage)</> : null}</>;
              })()}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPurchase} className="space-y-3" data-testid="purchase-form">
            <div className="space-y-1.5">
              <Label>Montant (€)</Label>
              <Input type="number" step="0.01" min="0.01" max={purchaseLead ? (purchaseLead.cagnotte?.total || 0) + (purchaseLead.cagnotte?.parrainage_bonus || 0) - (purchaseLead.cagnotte?.used || 0) : 50} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="purchase-amount" required />
            </div>
            <div className="space-y-1.5">
              <Label>Note (optionnel)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="purchase-note" placeholder="ex. Achat boulangerie du 15/01" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPurchaseLead(null)} className="rounded-xl">Annuler</Button>
              <Button type="submit" disabled={submitting} data-testid="purchase-submit" className="bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl">{submitting ? "..." : "Enregistrer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ManualLeadModal
        open={manualLeadOpen}
        onOpenChange={setManualLeadOpen}
        onCreated={() => reload()}
      />

    </div>
  );
}

export default ClientsPanel;
